import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import NotificationCenter from '@/components/NotificationCenter';
import WhatsNewPopover from '@/components/changelog/WhatsNewPopover';
import StreakFlame from '@/components/pkm/StreakFlame';
import OnboardingWizard from '@/components/OnboardingWizard';
import GuidedTourRunner from '@/components/GuidedTourRunner';
import {
  LayoutDashboard, Users, Calendar, FileText, CheckSquare, MessageCircle,
  Inbox, FolderOpen, Settings, Search, Mic, UserSearch,
  Share2, ClipboardList, Network, FileBarChart, ChevronLeft,
  ChevronRight, LogOut, User, Command, Menu, BarChart3, UsersRound, HelpCircle, Video, Shield, TrendingDown, Sparkles, UserCog, Brain, GraduationCap, ClipboardCheck, Zap, Mail, GitBranch, Globe, Smartphone
} from 'lucide-react';

// Nav items gated by role — see filter logic in sidebar render below
const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/chat', label: 'Chat', icon: MessageCircle },
  { path: '/teams', label: 'Teams', icon: UsersRound },
  { path: '/calendar', label: 'Calendar', icon: Calendar },
  { path: '/tasks', label: 'Tasks', icon: CheckSquare },
  { path: '/articles', label: 'Articles', icon: FileText },
  { path: '/inbox', label: 'Inbox', icon: Inbox },
  { path: '/workspace', label: 'Workspace', icon: FolderOpen },
  { divider: true, label: 'Tools' },
  { path: '/pkm', label: 'Knowledge Engine', icon: Brain },
  { path: '/ontology', label: 'Ontology', icon: GitBranch },
  { path: '/voice-journal', label: 'Voice Journal', icon: Mic },
  { path: '/expertise', label: 'Expertise Locator', icon: UserSearch },
  { path: '/knowledge-transfer', label: 'Knowledge Transfer', icon: Share2 },
  { path: '/cop', label: 'Community of Practice', icon: Users },
  { path: '/tests', label: 'Capability Assessment', icon: ClipboardList },
  { path: '/knowledge-maps', label: 'Knowledge Maps', icon: Network },
  { path: '/reports', label: 'Reports', icon: FileBarChart },
  { path: '/webinars', label: 'Webinars', icon: Video },
  { path: '/knowledge-audit', label: 'Knowledge Audit', icon: Shield },
  { path: '/knowledge-degradation', label: 'Degradation Tracking', icon: TrendingDown },
  { path: '/knowledge-deps', label: 'Dependencies', icon: Network },
  { path: '/knowledge-predictions', label: 'Gap Predictions', icon: Sparkles },
  { path: '/compliance', label: 'ISO 30401 Compliance', icon: ClipboardCheck },
  { divider: true, label: 'System' },
  { path: '/users', label: 'Users', icon: Users, staffOnly: true },
  { path: '/hr-workflows', label: 'HR Workflows', icon: UserCog, staffOnly: true },
  { path: '/analytics', label: 'Analytics', icon: BarChart3, adminOnly: true },
  { path: '/admin/automations', label: 'Automations', icon: Zap, adminOnly: true },
  { path: '/admin/email', label: 'Email Delivery', icon: Mail, adminOnly: true },
  { path: '/admin/kt-triggers', label: 'KT Triggers', icon: Share2, staffOnly: true },
  { path: '/settings', label: 'Settings', icon: Settings },
  { path: '/training', label: 'Training Guide', icon: GraduationCap },
  { path: '/help', label: 'Help Center', icon: HelpCircle },
  { path: '/mobile', label: 'Mobile App', icon: Smartphone },
];

// Roles that can see staff-only nav items (Users, HR Workflows)
const STAFF_ROLES = ['admin', 'manager', 'hr'];

const MainLayout = ({ children }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [unreadChat, setUnreadChat] = useState(0);
  const [copPending, setCopPending] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, token } = useAuth();

  // Poll global chat unread count every 30s to badge the nav icon
  useEffect(() => {
    if (!token) return;
    const fetchUnread = async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/unread`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) {
          const d = await r.json();
          setUnreadChat(d.total || 0);
        }
      } catch {}
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 30000);
    // Refresh when user returns to tab
    const onFocus = () => fetchUnread();
    window.addEventListener('focus', onFocus);
    // Also refresh when navigating away from /chat (likely marked read)
    const onPath = () => fetchUnread();
    if (location.pathname !== '/chat') onPath();
    return () => {
      clearInterval(t);
      window.removeEventListener('focus', onFocus);
    };
  }, [token, location.pathname]);

  // Poll CoP unattended signal count every 60s for the "Community of Practice" nav badge
  useEffect(() => {
    if (!token) return;
    const fetchCop = async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cop/signals/counts/unattended`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) {
          const d = await r.json();
          setCopPending(d.total || 0);
        }
      } catch { /* non-fatal */ }
    };
    fetchCop();
    const t = setInterval(fetchCop, 60000);
    const onFocus = () => fetchCop();
    window.addEventListener('focus', onFocus);
    if (location.pathname !== '/cop') fetchCop();
    return () => {
      clearInterval(t);
      window.removeEventListener('focus', onFocus);
    };
  }, [token, location.pathname]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-[#F3F4F6] flex" data-testid="main-layout">
      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed lg:sticky top-0 left-0 h-screen bg-white border-r border-[#E5E7EB] z-50 transition-all duration-200 ${
          sidebarCollapsed ? 'w-16' : 'w-64'
        } ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className={`h-16 flex items-center border-b border-[#E5E7EB] ${sidebarCollapsed ? 'justify-center px-2' : 'px-4'}`}>
            {sidebarCollapsed ? (
              <img src="/logo.png" alt="KStreak" className="w-8 h-8 rounded-lg object-contain" />
            ) : (
              <Link to="/dashboard" className="flex items-center gap-2">
                <img src="/logo.png" alt="KStreak" className="w-8 h-8 rounded-lg object-contain" />
                <span className="font-semibold text-[#0A0A0A]">KStreak</span>
              </Link>
            )}
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="space-y-1 px-2">
              {navItems.filter(item => {
                if (item.adminOnly && user?.role !== 'admin') return false;
                if (item.staffOnly && !STAFF_ROLES.includes(user?.role)) return false;
                return true;
              }).map((item, idx) => {
                if (item.divider) {
                  return sidebarCollapsed ? (
                    <div key={idx} className="h-px bg-[#E5E7EB] my-4" />
                  ) : (
                    <div key={idx} className="px-3 py-2 mt-4 first:mt-0">
                      <span className="text-xs uppercase tracking-[0.2em] font-medium text-[#9CA3AF]">
                        {item.label}
                      </span>
                    </div>
                  );
                }

                const Icon = item.icon;
                const isActive = location.pathname === item.path || 
                  (item.path !== '/dashboard' && location.pathname.startsWith(item.path));

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-medium transition-colors ${
                      isActive 
                        ? 'bg-[#002FA7] text-white' 
                        : 'text-[#4B5563] hover:bg-[#F3F4F6] hover:text-[#0A0A0A]'
                    } ${sidebarCollapsed ? 'justify-center' : ''}`}
                    data-testid={`nav-${item.path.replace('/', '')}`}
                    title={sidebarCollapsed ? item.label : undefined}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    {!sidebarCollapsed && <span className="flex-1">{item.label}</span>}
                    {!sidebarCollapsed && item.path === '/chat' && unreadChat > 0 && (
                      <span
                        className={`ml-auto text-[10px] font-bold min-w-[18px] h-[18px] px-1.5 rounded-full flex items-center justify-center ${isActive ? 'bg-white text-[#002FA7]' : 'bg-[#FF3B30] text-white'}`}
                        data-testid="nav-chat-unread-badge"
                      >
                        {unreadChat > 99 ? '99+' : unreadChat}
                      </span>
                    )}
                    {sidebarCollapsed && item.path === '/chat' && unreadChat > 0 && (
                      <span className="absolute -mt-4 ml-6 w-2 h-2 bg-[#FF3B30] rounded-full" data-testid="nav-chat-unread-dot" />
                    )}
                    {!sidebarCollapsed && item.path === '/cop' && copPending > 0 && (
                      <span
                        className={`ml-auto text-[10px] font-bold min-w-[18px] h-[18px] px-1.5 rounded-full flex items-center justify-center ${isActive ? 'bg-white text-[#002FA7]' : 'bg-[#FF9500] text-white'}`}
                        data-testid="nav-cop-pending-badge"
                        title={`${copPending} unattended CoP signal${copPending === 1 ? '' : 's'}`}
                      >
                        {copPending > 99 ? '99+' : copPending}
                      </span>
                    )}
                    {sidebarCollapsed && item.path === '/cop' && copPending > 0 && (
                      <span className="absolute -mt-4 ml-6 w-2 h-2 bg-[#FF9500] rounded-full" data-testid="nav-cop-pending-dot" />
                    )}
                  </Link>
                );
              })}
            </nav>
          </ScrollArea>

          {/* Collapse button */}
          <div className="hidden lg:flex justify-center p-2 border-t border-[#E5E7EB]">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="w-full"
              data-testid="sidebar-toggle"
            >
              {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Header */}
        <header className="h-16 bg-white border-b border-[#E5E7EB] flex items-center justify-between px-4 lg:px-6 sticky top-0 z-30" data-testid="header">
          {/* Mobile menu button */}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileMenuOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </Button>

          {/* Search bar */}
          <form onSubmit={handleSearch} className="flex-1 max-w-xl mx-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
              <Input
                type="search"
                placeholder="Search knowledge base... (Cmd+K)"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-12 bg-[#F3F4F6] border-[#E5E7EB] focus:bg-white"
                data-testid="global-search-input"
              />
              <kbd className="absolute right-3 top-1/2 -translate-y-1/2 hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 text-xs text-[#9CA3AF] bg-white border border-[#E5E7EB] rounded">
                <Command className="w-3 h-3" />K
              </kbd>
            </div>
          </form>

          {/* Notification bell + User menu */}
          <div className="flex items-center gap-2">
            <StreakFlame />
            <WhatsNewPopover />
            <NotificationCenter />
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2 px-2" data-testid="user-menu-trigger">
                <Avatar className="w-8 h-8">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="bg-[#002FA7] text-white text-sm">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden md:block text-sm font-medium text-[#0A0A0A]">
                  {user?.name}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-[#9CA3AF]">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="settings-menu-item">
                <User className="w-4 h-4 mr-2" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="settings-link">
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  // Copy the public Atlas link AND open it in a new tab — makes it one-click
                  // for users to share the benchmark atlas with prospects/colleagues.
                  const url = `${window.location.origin}/atlas`;
                  try { navigator.clipboard?.writeText?.(url); } catch { /* best-effort */ }
                  window.open('/atlas', '_blank', 'noopener,noreferrer');
                }}
                data-testid="public-atlas-link"
              >
                <Globe className="w-4 h-4 mr-2" />
                Public KM Atlas
                <span className="ml-auto text-[9px] text-[#9CA3AF] uppercase tracking-wider">Share</span>
              </DropdownMenuItem>
              {(user?.role === 'admin' || user?.role === 'hr') && (
                <DropdownMenuItem onClick={() => navigate('/admin/changelog')} data-testid="admin-changelog-link">
                  <Sparkles className="w-4 h-4 mr-2" />
                  Publish What's New
                </DropdownMenuItem>
              )}
              {(user?.role === 'admin' || user?.role === 'hr') && (
                <DropdownMenuItem onClick={() => navigate('/admin/benchmarks')} data-testid="admin-benchmarks-link">
                  <ClipboardCheck className="w-4 h-4 mr-2" />
                  KM Benchmarks
                </DropdownMenuItem>
              )}
              {(user?.role === 'admin' || user?.role === 'hr') && (
                <DropdownMenuItem onClick={() => navigate('/admin/sso')} data-testid="admin-sso-link">
                  <Shield className="w-4 h-4 mr-2" />
                  SSO Provisioning
                </DropdownMenuItem>
              )}
              {(user?.role === 'admin' || user?.role === 'hr') && (
                <DropdownMenuItem onClick={() => navigate('/admin/domain-migration')} data-testid="admin-domain-migration-link">
                  <Globe className="w-4 h-4 mr-2" />
                  Domain Migration
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-[#FF3B30]" data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-6" data-testid="main-content">
          {children}
        </main>
      </div>

      {/* Onboarding Wizard */}
      <OnboardingWizard />

      {/* Guided tour overlay — kicked off from TrainingPage via GuidedTourContext */}
      <GuidedTourRunner />
    </div>
  );
};

export default MainLayout;
